// Import charts, all with Chart suffix
import {
  LineChart,
  BarChart,
  PieChart,
  // ScatterChart,
  // RadarChart,
  // MapChart,
  // TreeChart,
  // TreemapChart,
  // GraphChart,
  // GaugeChart,
  // FunnelChart,
  // ParallelChart,
  // SankeyChart,
  // BoxplotChart,
  // CandlestickChart,
  // EffectScatterChart,
  // LinesChart,
  // HeatmapChart,
  // PictorialBarChart,
  // ThemeRiverChart,
  // SunburstChart,
  // CustomChart,
} from 'echarts/charts';
// import components, all suffixed with Component
import {
  // GridSimpleComponent,
  GridComponent,
  // PolarComponent,
  // RadarComponent,
  // GeoComponent,
  // SingleAxisComponent,
  // ParallelComponent,
  // CalendarComponent,
  // GraphicComponent,
  // ToolboxComponent,
  TooltipComponent,
  // AxisPointerComponent,
  // BrushComponent,
  TitleComponent,
  // TimelineComponent,
  // MarkPointComponent,
  // MarkLineComponent,
  // MarkAreaComponent,
  LegendComponent,
  // LegendScrollComponent,
  // LegendPlainComponent,
  // DataZoomComponent,
  // DataZoomInsideComponent,
  // DataZoomSliderComponent,
  // VisualMapComponent,
  // VisualMapContinuousComponent,
  // VisualMapPiecewiseComponent,
  // AriaComponent,
  // TransformComponent,
  // DatasetComponent,
} from 'echarts/components';
// Import renderer, note that introducing the CanvasRenderer or SVGRenderer is a required step
import {
  CanvasRenderer,
  // SVGRenderer,
} from 'echarts/renderers';
import { useCallback, useEffect, useRef, useMemo } from 'react';
import { EChartsType, getInstanceByDom, init, use } from 'echarts/core';
import { EChartsOption } from 'echarts';
import {
  Box,
  Flex,
  Text,
  theme,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { fontFamily } from './utils';
import merge from 'lodash/merge';
import { useTranslation } from 'react-i18next';

// Register the required components
use([
  LegendComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  PieChart,
  CanvasRenderer,
  BarChart,
  TitleComponent,
  // GraphicComponent,
]);

export interface EChartsProps extends EChartsOption {
  width?: string | number;
  height?: string | number;
  addChartEventListeners?: (chart: EChartsType) => void;
  removeChartEventListeners?: (chart: EChartsType) => void;
  chartRef?: React.MutableRefObject<EChartsType | null>;
}

export const ECharts = (props: EChartsProps) => {
  const {
    width,
    height,
    addChartEventListeners,
    removeChartEventListeners,
    chartRef: externalChartRef,
    ...option
  } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const chartRef = useRef<HTMLDivElement | null>(null);

  const resize = useCallback(() => {
    if (!chartRef.current) return;
    const chart = getInstanceByDom(chartRef.current);
    chart?.resize({ animation: { duration: 0 } });
  }, []);

  const addChartListeners = useCallback(() => {
    window.addEventListener('resize', resize);
    if (!chartRef.current) return;
    const chart = getInstanceByDom(chartRef.current);
    if (chart) addChartEventListeners?.(chart);
  }, [addChartEventListeners, resize]);

  const removeChartListeners = useCallback(() => {
    window.removeEventListener('resize', resize);
    if (!chartRef.current) return;
    const chart = getInstanceByDom(chartRef.current);
    if (chart) removeChartEventListeners?.(chart);
  }, [removeChartEventListeners, resize]);

  useEffect(() => {
    if (!chartRef.current) return;
    const chart =
      getInstanceByDom(chartRef.current) ??
      init(chartRef.current, colorMode, {
        useDirtyRect: true,
        renderer: 'canvas',
        useCoarsePointer: true,
        pointerSize: 50,
        width: width ?? 'auto',
        height: height ?? 'auto',
      });

    // Assign chart instance to external ref if provided
    if (externalChartRef) {
      externalChartRef.current = chart;
    }
    const mergedOption = merge(
      {
        backgroundColor: 'transparent',
        grid: { top: 20, right: 20, bottom: 20, left: 20 },
        xAxis: {
          type: 'category',
          axisLabel: {
            rotate: 30,
            margin: 12,
            fontFamily,
          },
          nameTextStyle: {
            fontFamily,
          },
          axisTick: {
            alignWithLabel: true,
          },
        },
        yAxis: {
          type: 'value',
          nameRotate: 90,
          nameLocation: 'middle',
          nameGap: 40,
          boundaryGap: ['0%', '1%'],
          nameTextStyle: {
            fontFamily,
          },
          axisLabel: {
            fontFamily,
          },
          minInterval: 1,
        },
        tooltip: {
          trigger: 'item',
          confine: false,
          textStyle: {
            fontSize: 13,
            fontFamily,
            color: (theme.colors as any)[colorStyle.color.primary],
          },
          backgroundColor: (theme.colors as any)[
            colorStyle.background.secondary
          ],
          borderWidth: 0,
          hideDelay: 500,
        },
        legend: {
          show: true,
          bottom: 20,
          align: 'left',
          textStyle: {
            fontFamily,
            color: (theme.colors as any)[colorStyle.color.primary],
          },
        },
      } satisfies EChartsOption,
      option
    );
    chart?.setOption(mergedOption);

    addChartListeners();

    return () => {
      chart?.dispose();
      removeChartListeners();
    };
  }, [
    addChartListeners,
    colorMode,
    colorStyle,
    height,
    option,
    removeChartListeners,
    width,
    externalChartRef,
  ]);

  const noData = useMemo(() => {
    if (Array.isArray(option.series)) {
      return option.series.length === 0;
    }
    return !option.series?.data;
  }, [option]);

  return noData ? (
    <Flex
      width={width ?? '100%'}
      height={height ?? '100%'}
      alignItems="center"
      justifyContent="center"
    >
      <Text color={colorStyle.color.secondary}>{t('common.noData')}</Text>
    </Flex>
  ) : (
    <Box ref={chartRef} width={width ?? '100%'} height={height ?? '100%'} />
  );
};
